import { useEffect, useState } from "react";
import { format, subDays } from "date-fns";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Skeleton from "@mui/material/Skeleton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import SendIcon from "@mui/icons-material/Send";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import { listDenials } from "~/services/admin/access";
import { COLORS } from "~/ui/tokens";
import { EmptyState, LoadErrorBanner } from "../shared/StatePanels";
import { denialReasonLabel, formatDateTimeBR } from "./labels";

export const denialsFrom = (now = new Date()) => format(subDays(now, 90), "yyyy-MM-dd");

// "Detalhe" (v2-pessoa-ficha-tentativas.png): e-mail do convite quando difere do usado (conta diferente).
export const denialDetail = (row) =>
  row.invitedEmail && row.invitedEmail.toLowerCase() !== (row.email || "").toLowerCase() ? `Convite para ${row.invitedEmail}` : "—";

const RESENDABLE = ["invite_expired", "invite_superseded"];

const REASON_STYLES = {
  invite_expired: { bg: "#fdecea", fg: "#d32f2f" },
  invite_email_mismatch: { bg: "#fff8e1", fg: "#b26a00" },
  account_mismatch: { bg: "#fff8e1", fg: "#b26a00" },
  invite_superseded: { bg: "#fff8e1", fg: "#b26a00" },
};
const DEFAULT_STYLE = { bg: "#eceff1", fg: "#455a64" };

export const paginationLabels = {
  labelRowsPerPage: "Linhas por página:",
  labelDisplayedRows: ({ from, to, count }) => `${from}–${to} de ${count}`,
  getItemAriaLabel: (type) => ({ first: "primeira página", last: "última página", next: "próxima página", previous: "página anterior" }[type]),
};

// reloadToken: muda quando a página recarrega dados (ex.: depois de convidar), para hasPendingInvite não ficar velho.
export default function DenialsTab({ onInvite, reloadToken = 0 }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ status: "loading", rows: [], total: 0 });

  useEffect(() => {
    const controller = new AbortController();
    setState((s) => ({ ...s, status: "loading" }));
    listDenials({ from: denialsFrom(), limit: rowsPerPage, offset: page * rowsPerPage }, { signal: controller.signal }).then(
      (result) => !controller.signal.aborted && setState({ status: "ok", ...result }),
      () => !controller.signal.aborted && setState((s) => ({ ...s, status: "error" }))
    );
    return () => controller.abort();
  }, [page, rowsPerPage, attempt, reloadToken]);

  if (state.status === "error") {
    return (
      <Box sx={{ p: 2 }}>
        <LoadErrorBanner message="Não foi possível carregar as tentativas. Tente novamente em instantes." onRetry={() => setAttempt((n) => n + 1)} />
      </Box>
    );
  }
  if (state.status === "ok" && state.total === 0) {
    return (
      <EmptyState
        icon={VerifiedUserOutlinedIcon}
        title="Nenhuma tentativa negada"
        text={"Ninguém sem convite tentou entrar nos últimos 90 dias.\nQuando acontecer, aparece aqui com o motivo."}
      />
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, px: 2, py: 1.5 }}>
        <Typography sx={{ fontWeight: 600, color: COLORS.primary }}>Tentativas negadas</Typography>
        <Typography variant="caption" sx={{ color: COLORS.muted }}>últimos 90 dias</Typography>
      </Box>
      <Table size="small" aria-label="Tentativas negadas">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>E-mail usado</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Data/hora</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Motivo</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Detalhe</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>IP</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {state.status === "loading" && state.rows.length === 0
            ? Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>{Array.from({ length: 6 }).map((__, j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>
              ))
            : state.rows.map((row) => {
                const style = REASON_STYLES[row.reason] || DEFAULT_STYLE;
                const canInvite = !row.hasPendingInvite && row.reason === "not_invited";
                // Reenvio pelo 409 INVITE_ALREADY_PENDING do diálogo de convite (P1-4).
                const canResend = row.hasPendingInvite && RESENDABLE.includes(row.reason);
                return (
                  <TableRow key={row.id} hover>
                    <TableCell sx={{ fontWeight: 500 }}>{row.email || "—"}</TableCell>
                    <TableCell>{formatDateTimeBR(row.createdAt)}</TableCell>
                    <TableCell><Chip size="small" label={denialReasonLabel(row.reason)} sx={{ bgcolor: style.bg, color: style.fg, fontWeight: 500 }} /></TableCell>
                    <TableCell sx={{ color: COLORS.muted }}>{denialDetail(row)}</TableCell>
                    <TableCell sx={{ color: COLORS.muted }}>{row.ip || "—"}</TableCell>
                    <TableCell align="right">
                      {canInvite && <Button size="small" variant="outlined" startIcon={<PersonAddAlt1OutlinedIcon />} onClick={() => onInvite?.(row.email, "invite")}>Convidar</Button>}
                      {canResend && <Button size="small" variant="outlined" startIcon={<SendIcon />} onClick={() => onInvite?.(row.email, "resend")}>Reenviar convite</Button>}
                    </TableCell>
                  </TableRow>
                );
              })}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={state.total}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[10, 25, 50]}
        onPageChange={(_, next) => setPage(next)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(Number(e.target.value));
          setPage(0);
        }}
        {...paginationLabels}
      />
    </Box>
  );
}
